import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_DICHTE, type Dichte, type Skin } from "@kontor/shared/ui/metrics";

/**
 * Skin and density.
 *
 * The customer asked for two skins: "Modern", and a "Klassik" mask that deliberately
 * resembles the character-mode software most of the industry still runs, so the
 * bookkeeper who has used it for twenty years is not alienated on day one. Both are
 * real skins, not a theme toggle — Klassik changes density, motion, radius and
 * typography together, because half-measures land in an uncanny valley that pleases
 * nobody.
 *
 * The choice lives on `<html data-skin data-density>` rather than in React state, so
 * the inline script in index.html can apply it before first paint and CSS variables
 * resolve without a re-render.
 */

interface SkinContextValue {
  skin: Skin;
  dichte: Dichte;
  setSkin: (skin: Skin) => void;
  setDichte: (dichte: Dichte) => void;
}

const SkinContext = createContext<SkinContextValue | null>(null);

const SKIN_KEY = "kontor.skin";
const DENSITY_KEY = "kontor.density";

function readInitial(): { skin: Skin; dichte: Dichte } {
  const root = document.documentElement;
  const skin: Skin = root.dataset.skin === "klassik" ? "klassik" : "modern";
  const raw = root.dataset.density;
  const dichte: Dichte =
    raw === "weit" || raw === "normal" || raw === "kompakt" || raw === "klassik"
      ? raw
      : DEFAULT_DICHTE[skin];
  return { skin, dichte };
}

export function SkinProvider({ children }: { children: ReactNode }) {
  const [{ skin, dichte }, setState] = useState(readInitial);

  useEffect(() => {
    document.documentElement.dataset.skin = skin;
    document.documentElement.dataset.density = dichte;
    try {
      localStorage.setItem(SKIN_KEY, skin);
      localStorage.setItem(DENSITY_KEY, dichte);
    } catch {
      /* private mode — the choice simply does not persist */
    }
  }, [skin, dichte]);

  const setSkin = useCallback((next: Skin) => {
    // Switching skin resets density to that skin's default: "kompakt" is nonsense
    // in Klassik, whose rows are already a fixed 20 px character grid.
    setState({ skin: next, dichte: DEFAULT_DICHTE[next] });
  }, []);

  const setDichte = useCallback((next: Dichte) => {
    setState((prev) => ({ ...prev, dichte: next }));
  }, []);

  const value = useMemo<SkinContextValue>(
    () => ({ skin, dichte, setSkin, setDichte }),
    [skin, dichte, setSkin, setDichte],
  );

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

export function useSkin(): SkinContextValue {
  const value = useContext(SkinContext);
  if (!value) throw new Error("useSkin must be used inside <SkinProvider>");
  return value;
}
